import {
  INSURANCE_TYPE_CFMR,
  INSURANCE_TYPE_PASSPORT_CARD,
  PASSPORT_CARD_INSURANCE_LABEL,
  PROVIDER_PASSPORT_CARD,
} from '~/consts/insurance';

import { dateNow, diffDays } from '~/services/TimeService';

/**
 * Detects Passport Card insurance items. Mirrors the backend's
 * `isPassportCardInsuranceItem` (svc-order), which matches on either the
 * provider or the insurance type, since older items may only carry one.
 */
export const isPassportCardInsurance = (item: { provider?: string | null; insurance_type?: string | null }): boolean =>
  item.provider === PROVIDER_PASSPORT_CARD || item.insurance_type === INSURANCE_TYPE_PASSPORT_CARD;

/**
 * Detects "Cancel For Many Reasons" (CFMR) items. CFMR is a cancellation-waiver
 * product grouped under insurance, so its copy is relabelled ("Travel
 * Protection") to avoid presenting it to agents as an insurance product.
 */
export const isCfmr = (item: { insurance_type?: string | null }): boolean =>
  item.insurance_type === INSURANCE_TYPE_CFMR;

/**
 * Label for the "Update" action on an insurance item — reads as "Travel
 * Protection" for CFMR, otherwise the standard insurance wording.
 */
export const getUpdateInsuranceLabel = (item: { insurance_type?: string | null }): string =>
  isCfmr(item) ? 'Update Travel Protection' : 'Update insurance';

/**
 * Product label for an insurance item in list views. PassportCard sells a single product
 * whose raw code is unreadable, so it gets a fixed short label; every other provider keeps
 * the product code admin has always shown.
 */
export const getInsuranceProductLabel = (item: {
  product_id?: string | null;
  provider?: string | null;
  insurance_type?: string | null;
}): string => (isPassportCardInsurance(item) ? PASSPORT_CARD_INSURANCE_LABEL : (item.product_id ?? ''));

/**
 * Whole-year age from a date of birth. Returns `null` for a missing/invalid DOB
 * so callers can render a blank field rather than "NaN".
 */
export const getAgeFromDateOfBirth = (dateOfBirth?: string | null): number | null => {
  if (!dateOfBirth) {
    return null;
  }
  const age = diffDays(dateNow(), dateOfBirth, 'year');
  return Number.isFinite(age) && age >= 0 ? Math.floor(age) : null;
};

export const getUpdatedPriceDiff = (
  isLPMember: boolean,
  platform: string,
  pricesDiff: { totalPriceDiff: number; luxPlusPriceDiff: number; mobilePriceDiff: number },
) => {
  if (isLPMember) {
    return pricesDiff.luxPlusPriceDiff;
  }

  if (platform === 'mobile') {
    return pricesDiff.mobilePriceDiff;
  }

  return pricesDiff.totalPriceDiff;
};
